/**
 * Settings Page Styles - BuzzBit X
 *
 * Shared styles for all settings components.
 */

import React from 'react';
import { tokens } from '../../design-system/tokens';

// Container styles — full available width beside the sidebar (no centered max-width).
// Horizontal padding is refined via `.account-page` in globalStyles.
export const containerStyles: React.CSSProperties = {
  padding: tokens.spacing[6],
  backgroundColor: tokens.colors.gray[50],
  minHeight: '100%',
  width: '100%',
  boxSizing: 'border-box',
};

export const headerStyles: React.CSSProperties = {
  marginBottom: tokens.spacing[8],
};

export const titleStyles: React.CSSProperties = {
  fontSize: tokens.typography.fontSize['3xl'],
  fontWeight: tokens.typography.fontWeight.bold,
  fontFamily: tokens.typography.fontFamily,
  color: tokens.colors.gray[900],
  marginBottom: tokens.spacing[2],
};

export const subtitleStyles: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.base,
  fontFamily: tokens.typography.fontFamily,
  color: tokens.colors.gray[600],
};

// Tab bar styles — pill row matching Contacts SegmentTabs (`/business?tab=contacts`)
export const tabBarStyles: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: tokens.spacing[2],
  marginBottom: tokens.spacing[8],
};

export const getTabStyles = (isActive: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem',
  padding: '0.375rem 0.75rem',
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  fontFamily: tokens.typography.fontFamily,
  color: isActive ? tokens.colors.white : tokens.colors.gray[600],
  backgroundColor: isActive ? tokens.colors.gray[900] : tokens.colors.white,
  border: isActive ? '1px solid transparent' : `1px solid ${tokens.colors.gray[200]}`,
  borderRadius: tokens.borderRadius.full,
  cursor: 'pointer',
  transition: 'color 150ms ease-in-out, background-color 150ms ease-in-out, border-color 150ms ease-in-out',
  whiteSpace: 'nowrap',
});

/**
 * Canonical Account card chrome — white, 1px gray-200 border, lg radius,
 * no shadow, p-5. Prefer `<SettingsCard>` for new UI; these objects remain
 * for sections still on inline styles. `flatCardStyles` === `cardStyles`
 * so Profile / Security / Support never diverge.
 */
export const cardStyles: React.CSSProperties = {
  backgroundColor: tokens.colors.white,
  borderRadius: tokens.borderRadius.lg,
  padding: tokens.spacing[5],
  border: `1px solid ${tokens.colors.gray[200]}`,
  boxShadow: 'none',
  marginBottom: tokens.spacing[5],
};

export const flatCardStyles: React.CSSProperties = { ...cardStyles };

export const cardTitleStyles: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.lg,
  fontWeight: tokens.typography.fontWeight.semibold,
  fontFamily: tokens.typography.fontFamily,
  color: tokens.colors.gray[900],
  marginBottom: tokens.spacing[4],
  lineHeight: '1.75rem',
};

export const cardDescriptionStyles: React.CSSProperties = {
  fontSize: tokens.typography.fontSize.sm,
  fontFamily: tokens.typography.fontFamily,
  color: tokens.colors.gray[500],
  marginTop: `-${tokens.spacing[2]}`,
  marginBottom: tokens.spacing[4],
  lineHeight: '1.25rem',
};

export const labelStyles: React.CSSProperties = {
  display: 'block',
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  fontFamily: tokens.typography.fontFamily,
  color: tokens.colors.gray[700],
  marginBottom: tokens.spacing[2],
};

/** Standard control height ≈ 44px for touch targets. */
export const inputStyles: React.CSSProperties = {
  width: '100%',
  minHeight: '44px',
  padding: `0.625rem ${tokens.spacing[4]}`,
  fontSize: tokens.typography.fontSize.sm,
  fontFamily: tokens.typography.fontFamily,
  color: tokens.colors.gray[900],
  backgroundColor: tokens.colors.white,
  border: `1px solid ${tokens.colors.gray[300]}`,
  borderRadius: tokens.borderRadius.md,
  outline: 'none',
  transition: 'border-color 200ms ease-in-out, box-shadow 200ms ease-in-out',
  boxSizing: 'border-box',
};

export const buttonStyles: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: tokens.spacing[2],
  minHeight: '44px',
  padding: `0.625rem ${tokens.spacing[6]}`,
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  fontFamily: tokens.typography.fontFamily,
  color: tokens.colors.white,
  backgroundColor: tokens.colors.accent[600],
  border: 'none',
  borderRadius: tokens.borderRadius.full,
  cursor: 'pointer',
  transition: 'background-color 200ms ease-in-out, opacity 200ms ease-in-out',
};

export const secondaryButtonStyles: React.CSSProperties = {
  ...buttonStyles,
  color: tokens.colors.gray[700],
  backgroundColor: tokens.colors.white,
  border: `1px solid ${tokens.colors.gray[300]}`,
  minHeight: '44px',
};

export const toggleStyles = (isOn: boolean): React.CSSProperties => ({
  position: 'relative',
  width: '44px',
  height: '24px',
  backgroundColor: isOn ? tokens.colors.accent[600] : tokens.colors.gray[300],
  borderRadius: tokens.borderRadius.full,
  cursor: 'pointer',
  transition: 'all 200ms ease-in-out',
});

export const toggleKnobStyles = (isOn: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: '2px',
  left: isOn ? '22px' : '2px',
  width: '20px',
  height: '20px',
  backgroundColor: tokens.colors.white,
  borderRadius: tokens.borderRadius.full,
  transition: 'all 200ms ease-in-out',
});

// Global CSS styles for the settings page
export const globalStyles = `
  .account-page {
    padding: ${tokens.spacing[4]};
  }

  @media (min-width: 640px) {
    .account-page {
      padding: ${tokens.spacing[6]};
    }
  }

  .support-help-card {
    transition: all 180ms ease-in-out;
  }

  .support-help-card:hover {
    border-color: ${tokens.colors.gray[300]};
    background-color: ${tokens.colors.gray[50]};
  }

  input:focus, select:focus, textarea:focus {
    border-color: ${tokens.colors.accent[500]};
    box-shadow: 0 0 0 3px ${tokens.colors.accent[100]};
  }

  input:disabled, select:disabled, textarea:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: ${tokens.colors.gray[50]};
  }

  .primary-button:hover:not(:disabled) {
    background-color: ${tokens.colors.accent[700]};
  }

  .primary-button:focus-visible,
  .secondary-button:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${tokens.colors.accent[100]};
  }

  .primary-button:disabled,
  .secondary-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .secondary-button:hover:not(:disabled) {
    background-color: ${tokens.colors.gray[50]};
  }

  .setting-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${tokens.spacing[3]};
    padding: ${tokens.spacing[4]};
    border: 1px solid ${tokens.colors.gray[200]};
    border-radius: ${tokens.borderRadius.md};
    margin-bottom: ${tokens.spacing[3]};
    transition: border-color 200ms ease-in-out, background-color 200ms ease-in-out;
  }

  @media (min-width: 640px) {
    .setting-row {
      align-items: center;
    }
  }

  .setting-row:hover {
    border-color: ${tokens.colors.accent[300]};
    background-color: ${tokens.colors.gray[50]};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes shimmer {
    100% { transform: translateX(100%); }
  }

  @media (prefers-reduced-motion: reduce) {
    .support-help-card,
    .setting-row,
    .primary-button,
    .secondary-button {
      transition: none;
    }
  }
`;
